import { useState } from 'react';
import { Text, Icon, Collapsible } from '@shopify/polaris';
import type { IconProps } from '@shopify/polaris';
import { ChevronDownIcon, ChevronRightIcon } from '@shopify/polaris-icons';

type IconSource = IconProps['source'];

export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle }: FAQItemProps) {
  return (
    <div
      style={{
        borderBottom: '1px solid #e1e3e5',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: '100%',
          padding: '16px 0',
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
        }}
      >
        <Text as="span" variant="bodyMd" fontWeight="semibold">
          {question}
        </Text>
        <div style={{ flexShrink: 0, color: '#637381' }}>
          <Icon source={isOpen ? ChevronDownIcon : ChevronRightIcon} />
        </div>
      </button>
      <Collapsible open={isOpen} id={`faq-${question}`}>
        <div style={{ paddingBottom: '16px' }}>
          <Text as="p" tone="subdued">
            {answer}
          </Text>
        </div>
      </Collapsible>
    </div>
  );
}

interface FAQCategoryProps {
  title: string;
  icon?: IconSource;
  color?: string;
  faqs: FAQ[];
  searchQuery?: string;
}

export function FAQCategory({ title, icon, color = '#9c6ade', faqs, searchQuery = '' }: FAQCategoryProps) {
  const [openFaqId, setOpenFaqId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const filteredFaqs = faqs.filter(
    (faq) =>
      searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (filteredFaqs.length === 0) {
    return null;
  }

  const toggleFaq = (faqId: string) => {
    setOpenFaqId(openFaqId === faqId ? null : faqId);
  };

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e1e3e5',
        overflow: 'hidden',
        marginBottom: '16px',
      }}
    >
      {/* Category Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          padding: '16px 20px',
          backgroundColor: isExpanded ? '#f9fafb' : 'white',
          border: 'none',
          borderBottom: isExpanded ? '1px solid #e1e3e5' : 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          textAlign: 'left',
        }}
      >
        {icon && (
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            <Icon source={icon} />
          </div>
        )}
        <div style={{ flex: 1 }}>
          <Text as="span" variant="headingSm" fontWeight="semibold">
            {title}
          </Text>
          <span style={{ marginLeft: '8px', color: '#637381', fontSize: '13px' }}>
            ({filteredFaqs.length} {filteredFaqs.length === 1 ? 'question' : 'questions'})
          </span>
        </div>
        <div style={{ color: '#637381' }}>
          <Icon source={isExpanded ? ChevronDownIcon : ChevronRightIcon} />
        </div>
      </button>

      {/* FAQ Items */}
      <Collapsible open={isExpanded} id={`faq-category-${title}`}>
        <div style={{ padding: '0 20px' }}>
          {filteredFaqs.map((faq) => (
            <FAQItem
              key={faq.id}
              question={faq.question}
              answer={faq.answer}
              isOpen={openFaqId === faq.id}
              onToggle={() => toggleFaq(faq.id)}
            />
          ))}
        </div>
      </Collapsible>
    </div>
  );
}

interface FAQListProps {
  faqs: FAQ[];
  searchQuery?: string;
  categories: Array<{
    id: string;
    title: string;
    icon?: IconSource;
    color?: string;
  }>;
}

export function FAQList({ faqs, searchQuery = '', categories }: FAQListProps) {
  return (
    <div>
      {categories.map((category) => {
        const categoryFaqs = faqs.filter((faq) => faq.category === category.id);
        return (
          <FAQCategory
            key={category.id}
            title={category.title}
            icon={category.icon}
            color={category.color}
            faqs={categoryFaqs}
            searchQuery={searchQuery}
          />
        );
      })}
    </div>
  );
}
